/*
    Creation Time: 2023 - Oct - 21
    Created by:  (hamidrezakk)
    Maintainers:
       1.  HamidrezaKK (hamidrezakks@gmail.com)
    Auditor: HamidrezaKK
    Copyright Qlub_ 2024
*/

import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import AuthManager from '../../repository/auth';
import { recaptchaEnable, recaptchaSiteKey } from '../../repository/axios/constants';
import { IAuthContext, ILoginCallbacks, ILoginOptions, ILogoutCallbacks } from './types';
import { deleteAuth, getAuth, setAuth } from '../../service/session/auth';
import { useQlubRouter } from '../../hook/router';
import Login from '../../component/Login';
import { IProfile } from '../../repository/auth/type';
import * as events from '../../const/events';
import { qlubSessionStorage } from '../../service/storage/sessionStorage';
import { getRedirection, removeRedirection } from '../../component/Login/DataManager/utils';
import { useTranslation } from '../../hook/translations';
import { snackBar } from '../../hook/snackBar';
import VendorAPIManager from '../../repository/vendor';
import { getForceEnable, setForceEnable } from './utils';

const recaptchaTimeout = 5000;

const authNewUserKey = 'qlub.auth.newUser';

const AuthContext = createContext<IAuthContext>({
    getRecaptchaToken: () => Promise.reject(new Error('not implemented')),
    login: () => Promise.reject(new Error('not implemented')),
    isLogin: false,
    setIsLogin: () => undefined,
    logout: () => undefined,
    refetchProfile: () => Promise.reject(new Error('not implemented')),
    setDrawerConstants: () => undefined,
    isAuthorizedUser: false,
    storeAuthToken: () => undefined,
    setIsNewUserStorage: () => Promise.reject(new Error('not implemented')),
    getIsNewUserStorage: () => false,
    removeIsNewUserStorage: () => undefined,
    withSSOLogin: true,
    loginWidgets: null,
    setLoginWidgets: () => console.log('not implemented'),
    setWithSSOLogin: () => undefined,
    forceEnableLogin: false,
    setForceEnableLogin: () => undefined,
    loginCallbacks: {
        preLogin: async () => true,
        postOTPVerification: async () => undefined,
    },
    setLoginCallbacks: () => undefined,
    logoutCallbacks: [],
    setLogoutCallbacks: () => undefined,
    setLoginVendorLogo: () => console.log('not implemented'),
});

interface IProps {
    children?: ReactNode;
}

export interface IDrawerContent {
    title: string;
    description: string;
    buttonText: string;
}

export interface IDrawerConstants {
    text: {
        phone: IDrawerContent;
        otp: IDrawerContent;
        done: IDrawerContent;
    };
    numInputs: number;

    termsAndConditions?: {
        show: boolean;
        content?: ReactNode;
        defaultChecked?: boolean;
        label: string;
    };

    [key: string]: any;
}

export function AuthContextProvider({ children }: IProps) {
    const { t } = useTranslation('common');

    const AuthData = getAuth();
    const authManager = AuthManager.getInstance();
    const { isReady, pathname, url, lang, routerReplace } = useQlubRouter();
    const [enableRecaptcha, setEnableRecaptcha] = useState(false);
    const [reserveRecaptcha, setReserveRecaptcha] = useState(false);
    const promise = useRef<{
        resolve?: any;
        reject?: any;
    }>({});
    const loginFn = useRef<undefined | ((options?: ILoginOptions) => Promise<any>)>(undefined);
    const [isLogin, setIsLogin] = useState<boolean>(Boolean(AuthData.JWTToken));
    const [isAuthorizedUser, setIsAuthorized] = useState<boolean>(Boolean(AuthData.JWTToken && AuthData.isAuthorized));
    const [drawerConstants, setDrawerConstants] = useState<IDrawerConstants | undefined>();
    const [isCalledFromHeader, setIsCalledFromHeader] = useState<boolean>(false);
    const [profile, setProfile] = useState<IProfile | undefined>(undefined);
    const timeoutRef = useRef<any>(null);
    const [withSSOLogin, setWithSSOLogin] = useState(false);
    const [forceEnableLogin, setForceEnableLogin] = useState(getForceEnable() || false);
    const [loginCallbacks, setLoginCallbacks] = useState<ILoginCallbacks>({});
    const [logoutCallbacks, setLogoutCallbacks] = useState<ILogoutCallbacks>([]);
    const [loginWidgets, setLoginWidgets] = useState<React.ReactNode | null>(null);
    const [loginVendorLogo, setLoginVendorLogo] = useState<string | null>(null);

    const { triggerAlternateSnackBar } = snackBar();

    const forceEnableLoginHandler = useCallback((enable: boolean) => {
        setForceEnable(enable);
        setForceEnableLogin(enable);
    }, []);

    useEffect(() => {
        if (!isReady || !pathname) {
            return;
        }

        timeoutRef.current = setTimeout(() => {
            setEnableRecaptcha(false);
            setReserveRecaptcha(false);
        }, 3000);
    }, [isReady, pathname]);

    useEffect(() => {
        if (!isAuthorizedUser) {
            return;
        }

        getProfileData();
    }, []);

    /**
     * When user redirected to c-app from google login,
     * this effect will remove the redirection from the session storage if matches
     */
    useEffect(() => {
        if (window.location.href !== getRedirection()) {
            return;
        }

        removeRedirection();
    }, [url]);

    const setLoginWidgetsHandler = useCallback((node: React.ReactNode | null) => {
        setLoginWidgets(node);
    }, []);

    const setLoginVendorLogoHandler = useCallback((img: string | null) => {
        setLoginVendorLogo(img);
    }, []);

    const getProfileData = async () => {
        try {
            const data = await authManager.getProfile();
            setProfile(data);
        } catch (err: any) {
            if (
                (err?.response?.data?.items === 'JWT_UPGRADE_REQUIRED' ||
                    err?.response?.data?.message === 'JWT_UPGRADE_REQUIRED') &&
                err?.response?.data?.code === 417
            ) {
                authManager.upgradeToken({ JWTToken: AuthData.JWTToken }).then((res) => {
                    setAuth(res);
                    setProfile(res.profile);
                });
            }
        }
    };

    const recaptchaRef = (ref: ReCAPTCHA) => {
        if (!ref) {
            return;
        }

        const timeout = setTimeout(() => {
            promise.current?.reject?.(new Error(`recaptcha took more than ${recaptchaTimeout} milliseconds`));
            promise.current = {};
        }, recaptchaTimeout);

        ref.executeAsync()
            .then((token: any) => {
                promise.current?.resolve?.(token);
                promise.current = {};
            })
            .catch((err: Error) => {
                promise.current?.reject?.(err);
                promise.current = {};
            })
            .finally(() => {
                if (!reserveRecaptcha) {
                    setEnableRecaptcha(false);
                }
                clearTimeout(timeout);
            });
    };

    const getRecaptchaToken = useCallback((reserve?: boolean, forceEnable?: boolean): Promise<string | null> => {
        if (!recaptchaEnable && !forceEnable) {
            return Promise.resolve('__ignore__');
        }

        clearTimeout(timeoutRef.current);
        setEnableRecaptcha(reserve || false);
        setReserveRecaptcha(true);
        setTimeout(() => {
            setEnableRecaptcha(true);
        }, 100);
        return new Promise((resolve, reject) => {
            if (promise.current) {
                promise.current.resolve = resolve;
                promise.current.reject = reject;
            }
        });
    }, []);

    const login = useCallback(
        (options?: ILoginOptions) => {
            if (isLogin && isAuthorizedUser) {
                return Promise.resolve(AuthData);
            }

            if (options?.isFullPage) {
                setIsCalledFromHeader(true);
            }

            if (!options?.guest) {
                if (!loginFn.current) {
                    return Promise.reject(new Error('no implemented'));
                }

                return loginFn.current(options);
            }

            return getRecaptchaToken(true).then((token) => {
                if (!token) {
                    return Promise.reject(new Error('empty token'));
                }

                const vendor = VendorAPIManager.getInstance().getVendorCache(url);

                return authManager
                    .createToken({
                        reCaptcha: token,
                        restaurantUnique: vendor?.unique,
                        countryCode: vendor?.country?.code,
                    })
                    .then((res) => {
                        setAuth(res);
                        setIsLogin(true);
                        if (res.isAuthorized) {
                            setProfile(res.profile);
                            setIsAuthorized(true);
                            document.dispatchEvent(new CustomEvent(events.login));
                        }
                        return res;
                    });
            });
        },
        [isLogin, isAuthorizedUser],
    );

    const logout = useCallback(() => {
        deleteAuth();
        setIsLogin(false);
        setProfile(undefined);
        logoutCallbacks.forEach((cb) => cb());
        routerReplace('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]', { lang }).then(() => {
            window.location.reload();
        });
    }, [pathname, logoutCallbacks?.length]);

    const setIsNewUserStorage = useCallback((isNewUser: boolean) => {
        qlubSessionStorage.set(authNewUserKey, isNewUser);
    }, []);

    const getIsNewUserStorage = useCallback(() => {
        return qlubSessionStorage.get(authNewUserKey);
    }, []);

    const removeIsNewUserStorage = useCallback(() => {
        qlubSessionStorage.remove(authNewUserKey);
    }, []);

    const setWithSSOLoginHandler = useCallback((val: boolean) => {
        setWithSSOLogin(val);
    }, []);

    const storeAuthToken = useCallback(
        (
            { refresh, access, isAuthorized = true }: { access: string; refresh: string; isAuthorized?: boolean },
            silent?: boolean,
        ) => {
            setAuth({
                JWTToken: access,
                refresh,
                isAuthorized,
            });

            setIsLogin(true);
            setIsAuthorized(isAuthorized);
            getProfileData();

            if (!silent && isAuthorized) {
                if (getIsNewUserStorage() === true) {
                    triggerAlternateSnackBar(t('Your qlub account is created!'));
                } else {
                    triggerAlternateSnackBar(t('You are logged in successfully'));
                }
            }

            removeIsNewUserStorage();
        },
        [lang],
    );

    return (
        <AuthContext.Provider
            value={{
                getRecaptchaToken,
                login,
                isLogin,
                setIsLogin,
                logout,
                refetchProfile: getProfileData,
                setDrawerConstants,
                profile,
                isAuthorizedUser,
                storeAuthToken,
                setIsNewUserStorage,
                getIsNewUserStorage,
                removeIsNewUserStorage,
                withSSOLogin,
                setWithSSOLogin: setWithSSOLoginHandler,
                loginWidgets,
                setLoginWidgets: setLoginWidgetsHandler,
                forceEnableLogin,
                setForceEnableLogin: forceEnableLoginHandler,
                loginCallbacks,
                setLoginCallbacks,
                logoutCallbacks,
                setLogoutCallbacks,
                setLoginVendorLogo: setLoginVendorLogoHandler,
            }}
        >
            <Login
                drawerConstants={drawerConstants}
                loginWidgets={loginWidgets}
                getRecaptchaToken={getRecaptchaToken}
                setOpenFn={(fn: any) => {
                    loginFn.current = fn;
                }}
                fullPage={isCalledFromHeader}
                onLogin={(loginArg, profileArg) => {
                    setIsLogin(loginArg);
                    setProfile(profileArg);
                    if (loginArg && profileArg) {
                        setIsAuthorized(true);
                    }
                }}
                loginCallbacks={loginCallbacks}
                loginVendorLogo={loginVendorLogo}
            />

            {recaptchaSiteKey && enableRecaptcha && (
                <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaSiteKey} size="invisible" />
            )}

            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
